<template>
  <div ref="el" class="io-wrapper">
    <slot />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  threshold:  { type: Number, default: 0.2 },
  rootMargin: { type: String, default: '50px' },
  direction:  {
    type: String,
    default: '',
    validator: v => ['up','down','left','right',''].includes(v)
  },
  once:       { type: Boolean, default: false },
  delay:      { type: Number, default: 0 }
})

const el = ref(null)
const observerRegistry = new Map()

function getObserver({ threshold, rootMargin }) {
  const key = `${threshold}|${rootMargin}`
  if (!observerRegistry.has(key)) {
    const callbacks = new WeakMap()
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const cb = callbacks.get(entry.target)
        if (cb) cb(entry)
      }
    }, { threshold, rootMargin })
    observerRegistry.set(key, { observer, callbacks })
  }
  return observerRegistry.get(key)
}

onMounted(() => {
  const { observer, callbacks } = getObserver(props)

  if (!el.value) return

  if (props.delay > 0) {
    el.value.style.transitionDelay = `${props.delay}s`
  }

  const slideCls = props.direction ? `slide-${props.direction}` : 'fade-only'
  el.value.classList.add(slideCls)

  const ioCallback = entry => {
    const node = entry.target
    if (entry.isIntersecting) {
      node.classList.add('is-visible')
      if (props.once) {
        callbacks.delete(node)
        observer.unobserve(node)
      }
    } else if (!props.once) {
      node.classList.remove('is-visible')
    }
  }

  callbacks.set(el.value, ioCallback)
  observer.observe(el.value)
})

onBeforeUnmount(() => {
  const { observer, callbacks } = getObserver(props)
  if (el.value) {
    observer.unobserve(el.value)
    callbacks.delete(el.value)
  }
})
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;

.io-wrapper {
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4,0,0.2,1);
  will-change: transform, opacity;

  &.is-visible {
    opacity: 1;
    transform: translate3d(0,0,0) !important;
  }
  &.slide-up    { transform: translate3d(0,50px,0); }
  &.slide-down  { transform: translate3d(0,-50px,0); }
  &.slide-left  { transform: translate3d(50px,0,0); }
  &.slide-right { transform: translate3d(-50px,0,0); }
  &.fade-only   { transform: none; }
}
</style>
